import { useApiMutation } from "@gladis/api-client";
import { formatEur, type PropertyListDto } from "@gladis/shared";
import { Button, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { PROPERTIES_KEY, useProperties } from "./PropertyList.js";

/**
 * Properties the household has archived, behind a disclosure.
 *
 * ============================================================================
 * REVEALING THEM IS A DISCLOSURE, NOT A FILTER
 * ============================================================================
 *
 * The totals above the active list count active properties only, and expanding this section must
 * not change them. That is structural rather than promised: the server returns `active` and
 * `archived` in ONE response with the totals already computed from `active`, so there is no
 * arithmetic here that could accidentally include an archived row.
 *
 * A SOLD PROPERTY LOOKS EXACTLY LIKE ANY OTHER ARCHIVED ONE, and that is the clarification
 * session's decision rather than an oversight. The sale is recorded in the final valuation and in a
 * `property.sold` activity-log entry; until feature 012 ships the log reader there is no way to see
 * which of these were sales. Written here so it reads as a known consequence.
 */
export function ArchivedProperties(): ReactNode {
  const t = useT();
  const properties = useProperties();
  const [open, setOpen] = useState(false);

  const unarchive = useApiMutation<{ restoredPartIds: string[] }, { propertyId: string }>(
    (client, { propertyId }) =>
      client.post<{ restoredPartIds: string[] }>(`/properties/${propertyId}/unarchive`, {}),
    { invalidates: [PROPERTIES_KEY] },
  );

  const archived: PropertyListDto["archived"] = properties.data?.archived ?? [];
  if (archived.length === 0) return null;

  return (
    /*
     * A card like the rest, so an archived property does not read as a stray control on the page
     * background. It keeps its own collapsed treatment — the count is on the toggle, which is how
     * investments' closed positions do it, so the two modules do not invent different answers to
     * "things that are still here but finished".
     */
    <section className="rounded-control border-line bg-panel flex flex-col gap-3 border p-5">
      <Button
        onClick={() => {
          setOpen((shown) => !shown);
        }}
      >
        {open ? t("prop.hide") : t("prop.show")} {archived.length} archived{" "}
        {archived.length === 1 ? "property" : "properties"}
      </Button>

      {open ? (
        <>
          <p className="text-sm text-muted">{t("prop.archived.hint")}</p>
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>{t("prop.list.colProperty")}</Table.HeaderCell>
                <Table.HeaderCell numeric>{t("prop.archived.lastKnownValue")}</Table.HeaderCell>
                <Table.HeaderCell> </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {archived.map((property) => (
                <Table.Row key={property.id}>
                  <Table.Cell>
                    <span className="flex flex-col">
                      <span className="font-medium">
                        {property.address.street} {property.address.houseNumber}
                        {property.address.houseAddition ?? ""}
                      </span>
                      <span className="text-sm text-muted">{property.address.city}</span>
                    </span>
                  </Table.Cell>
                  <Table.Cell numeric>{formatEur(property.currentValue)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      onClick={() => {
                        unarchive.mutate({ propertyId: property.id });
                      }}
                      disabled={unarchive.isPending}
                    >
                      {t("exp.card.restore")}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      ) : null}
    </section>
  );
}
